import {
  EnumProductType,
  OrderDeliveryMethodInput,
  useBookerFulfillmentOptionsQuery,
} from "gql/generated";
import { useBookerContext, useCartContext } from "widgets/booker/contexts";

/**
 * Mirrors backend `isDeliveryRequired`: any merch in the cart plus pickup or
 * shipping enabled on the storefront. Staff-entered and payment-request
 * checkouts skip delivery entirely.
 */
export const useDeliveryMethod = () => {
  const { booker, invoiceAdminMode, paymentRequestId } = useBookerContext();
  const { cart, delivery, updateDelivery, shippingTotal, deliveryAvailable } =
    useCartContext();

  const merchUnits = cart
    .filter(item => item.productType === EnumProductType.Merch)
    .reduce((sum, item) => sum + (item.quantity || 0), 0);
  const applies = merchUnits > 0 && !invoiceAdminMode && !paymentRequestId;

  const { data, loading } = useBookerFulfillmentOptionsQuery({
    variables: { bookerId: booker?._id ?? "" },
    skip: !applies || !booker?._id,
  });
  const options = data?.bookerFulfillmentOptions;
  const pickupLocations = (options?.pickupLocations ?? []).filter(
    (l): l is NonNullable<typeof l> => !!l,
  );
  const pickupEnabled = !!options?.pickupEnabled && pickupLocations.length > 0;
  const shippingEnabled = !!options?.shippingEnabled;
  const required = applies && (pickupEnabled || shippingEnabled);

  const address = delivery?.shippingAddress;
  const hasCompleteAddress =
    !!address?.country?.trim() &&
    !!address?.line1?.trim() &&
    !!address?.postalCode?.trim();

  const isComplete =
    !required ||
    (delivery?.method === OrderDeliveryMethodInput.Pickup &&
      pickupLocations.some(l => l._id === delivery.pickupLocationId)) ||
    (delivery?.method === OrderDeliveryMethodInput.Ship &&
      shippingEnabled &&
      hasCompleteAddress &&
      deliveryAvailable === true);

  return {
    required,
    loading,
    isComplete,
    pickupEnabled,
    pickupLocations,
    shippingEnabled,
    delivery,
    updateDelivery,
    shippingTotal,
    deliveryAvailable,
    hasCompleteAddress,
  };
};
